'use client';

import { trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import { getValidatedQueryParameters } from '@svenskaspelab/poolbetting/core/lib/client';
import type { SheetTypes } from '@svenskaspelab/poolbetting/schema/input';
import { TipsenLink } from '@svenskaspelab/poolbetting/ui/TipsenLink';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { type ComponentProps, type MouseEvent, type ReactNode, useRef } from 'react';

import { dispatchCloseSheet, dispatchOpenSheet } from '@/lib/index.browser';

type OpenSheetLinkProps = Omit<
  ComponentProps<typeof TipsenLink>,
  'children' | 'href' | 'replace' | 'scroll' | 'onClick'
> & {
  sheet: SheetTypes;
  children?: ReactNode;
  trackingObject?: {
    category: string;
    action: string;
    optLabel?: string;
  };
};

/**
 * Link that opens sheet optimistically on click and then updates query state immediately.
 *
 * @param root0 Component props
 * @param root0.sheet Sheet type to open
 * @param root0.trackingObject Optional object for tracking the click event, with properties category, action, and optLabel
 * @param root0.children Link content
 * @returns Link component
 */
export default function OpenSheetLink({ sheet, trackingObject, children, ...props }: OpenSheetLinkProps): ReactNode {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isPendingRef = useRef(false);

  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (trackingObject) {
      trackEvent(trackingObject);
    }

    if (event.defaultPrevented) {
      return;
    }

    event.preventDefault();

    // Debounce: ignore rapid repeated clicks until current navigation settles
    if (isPendingRef.current) {
      return;
    }

    isPendingRef.current = true;
    window.setTimeout(() => {
      isPendingRef.current = false;
    }, 500);

    const { sheet: currentSheet } = getValidatedQueryParameters('sheet')(searchParams);

    const isSheetAlreadyOpen = currentSheet === sheet;

    // If sheet is already open, close it immediately
    if (isSheetAlreadyOpen) {
      dispatchCloseSheet();

      const qs = new URLSearchParams(searchParams);
      qs.delete('sheet');

      const newPath = qs.toString() ? `${pathname}?${qs.toString()}` : pathname;
      router.replace(newPath, { scroll: false });
      return;
    }

    // Otherwise, open it optimistically and sync URL
    dispatchOpenSheet(sheet);

    const qs = new URLSearchParams(searchParams);
    qs.set('sheet', sheet);

    const newPath = qs.toString() ? `${pathname}?${qs.toString()}` : pathname;
    router.replace(newPath, { scroll: false });
  };

  return (
    <TipsenLink
      {...props}
      onClick={handleClick}
      href={{
        query: {
          sheet,
        },
      }}
    >
      {children}
    </TipsenLink>
  );
}
